import { apiClient } from '@/services/api-client';
import { logger } from '@/lib/logger';

export interface SubscriptionPlanOption {
  id: string;
  name: string;
  planType?: string | null;
  isTeamPlan?: boolean;
  isCurrentPlan?: boolean;
  requiresWorkspaceNameForSwitch?: boolean;
  parentPlanId?: string | null;
  isVariant?: boolean;
  pricing?: Array<{
    planId?: string | null;
    priceId?: string | null;
    billingCycleId: string;
    billingCycleName: string;
    billingCycleMonths: number | null;
    isCurrentSelection?: boolean;
  }>;
}

interface SubscriptionPlanOptionResource {
  id: string;
  type?: string;
  attributes?: Record<string, unknown>;
  [key: string]: unknown;
}

interface SubscriptionPlanOptionsResponse {
  data?: SubscriptionPlanOptionResource[];
}

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;
const readBoolean = (value: unknown): boolean =>
  typeof value === 'boolean' ? value : false;

export const normalizeSubscriptionPlanOptionResource = (
  resource: SubscriptionPlanOptionResource,
): SubscriptionPlanOption | null => {
  const resourceId = readString(resource.id);
  if (!resourceId) {
    logger.warn('[subscription-plan-options] Skipping plan option with missing id.', resource);
    return null;
  }

  const attributes = (resource.attributes && typeof resource.attributes === 'object')
    ? (resource.attributes as Record<string, unknown>)
    : (resource as Record<string, unknown>);
  if (!attributes || typeof attributes !== 'object') {
    logger.warn('[subscription-plan-options] Skipping plan option with invalid attributes.', resource);
    return null;
  }

  const name = readString(attributes.name) ?? '';
  const planType = readString(attributes.plan_type ?? attributes.planType) ?? null;
  const isTeamPlan = readBoolean(attributes.is_team_plan ?? attributes.isTeamPlan);
  const isCurrentPlan = readBoolean(attributes.is_current_plan ?? attributes.isCurrentPlan);
  const requiresWorkspaceNameForSwitch = readBoolean(
    attributes.requires_workspace_name_for_switch ?? attributes.requiresWorkspaceNameForSwitch,
  );
  const parentPlanId = readString(attributes.parent_plan_id ?? attributes.parentPlanId) ?? null;
  const isVariant = readBoolean(attributes.is_variant ?? attributes.isVariant);
  const pricingRaw = Array.isArray(attributes.pricing) ? attributes.pricing : [];
  const pricing = pricingRaw
    .map((entry: unknown) => {
      if (!entry || typeof entry !== 'object') return null;
      const record = entry as Record<string, unknown>;
      const billingCycleId = readString(record.billing_cycle_id ?? record.billingCycleId);
      const billingCycleName = readString(record.billing_cycle_name ?? record.billingCycleName) ?? '';
      const planId = readString(record.plan_id ?? record.planId) ?? null;
      const priceId = readString(record.price_id ?? record.priceId) ?? readString(record.id) ?? null;
      const monthsRaw = record.billing_cycle_months ?? record.billingCycleMonths;
      const billingCycleMonths =
        typeof monthsRaw === 'number' ? monthsRaw : typeof monthsRaw === 'string' ? Number(monthsRaw) : null;
      const isCurrentSelection = readBoolean(
        record.is_current_selection ?? record.isCurrentSelection,
      );
      if (!billingCycleId) return null;
      return {
        planId,
        priceId,
        billingCycleId,
        billingCycleName,
        billingCycleMonths: Number.isFinite(billingCycleMonths) ? billingCycleMonths : null,
        isCurrentSelection,
      };
    })
    .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));

  return {
    id: resourceId,
    name,
    planType,
    isTeamPlan,
    isCurrentPlan,
    requiresWorkspaceNameForSwitch,
    parentPlanId,
    isVariant,
    pricing,
  };
};

class SubscriptionPlanOptionsService {
  async list(params?: { productId?: string; customerId?: string }): Promise<SubscriptionPlanOption[]> {
    const { productId, customerId } = params ?? {};
    const response = await apiClient.get<SubscriptionPlanOptionsResponse>(
      '/api/v1/billing/subscription-plans/options',
      productId || customerId
        ? { params: { ...(productId ? { product_id: productId } : {}), ...(customerId ? { customer_id: customerId } : {}) } }
        : undefined,
    );
    const resources = Array.isArray(response.data?.data) ? response.data?.data : [];
    return resources
      .map(normalizeSubscriptionPlanOptionResource)
      .filter((option): option is SubscriptionPlanOption => Boolean(option?.id));
  }
}

export const subscriptionPlanOptionsService = new SubscriptionPlanOptionsService();
